import { ApiError } from '@/lib/apiClient';

/**
 * How a failed query should be presented. The slice's rule: never offer "a retry
 * that cannot work".
 *
 * - `suspended` — **402 `tenant_suspended`** (the D+14 view-hide; data keeps
 *   collecting). A real, different, actionable state with its own honest copy.
 *   Pressing retry cannot change it, so no retry.
 *
 *   🔴 IT IS 402, NOT 403, AND THIS FILE SAID 403 UNTIL IT WAS CHECKED AGAINST
 *   THE API. `requireTenant` returns **402** `tenant_suspended`
 *   (`api/src/modules/tenancy/middleware.ts:151-152`) and **403**
 *   `tenant_context_required` for a STAFF principal on a tenant route
 *   (`middleware.ts:131-133`). Mapping 403 → suspended was inverted twice over:
 *   the suspended copy could never render on an actual suspension, and a staff
 *   principal would have been told THEIR TENANT IS SUSPENDED — which is not
 *   merely unhelpful, it is a false statement about a customer's billing state,
 *   shown to the one person best placed to act on it.
 *
 *   403 is now `permanent`. After this slice a staff principal is intercepted at
 *   `RequireAuth` and shown `StaffPanel` before any screen queries, so 403 here
 *   is the honest fallback for a case the panel did not catch, not a state with
 *   its own copy.
 *
 * - `permanent` — any other non-transient 4xx (400 / 401 / 403 / 404 / 405 …).
 *   The answer will not change on a repeat, so no retry.
 * - `transient` — 408 and 429 (Mahad's explicit decision: these ARE transient),
 *   every 5xx, and anything that is not an ApiError at all (a fetch TypeError, a
 *   JSON parse failure). Keep the retry.
 *
 * 401 lands here as `permanent`, but in practice a refresh-failure forces logout
 * before a screen renders it; classifying it no-retry is the honest fallback if
 * one ever does reach a screen.
 *
 * Lives in a plain `.ts` module (not the component file) so screens with a
 * bespoke error frame — the live-map overlay, the vehicle-detail panel — can
 * import the DECISION without pulling in the component.
 */
export type QueryErrorKind = 'suspended' | 'permanent' | 'transient';

export function classifyQueryError(error: unknown): QueryErrorKind {
  if (error instanceof ApiError) {
    if (error.status === 402) return 'suspended';
    if (error.status === 408 || error.status === 429) return 'transient';
    if (error.status >= 400 && error.status < 500) return 'permanent';
  }
  return 'transient';
}

/** True when the error is one a retry could plausibly resolve. */
export function isRetryable(error: unknown): boolean {
  return classifyQueryError(error) === 'transient';
}
